import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { BiometryType } from '@capgo/capacitor-native-biometric';
import {
  IonCard,
  IonIcon,
  IonImg,
  IonLabel,
  IonPage,
  isPlatform,
} from '@ionic/react';
import classNames from 'classnames';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import FaceIdLogo from '@/assets/icons/face-id.svg';
import FingerprintLogo from '@/assets/icons/fingerprint.svg';
import GreenCardImg from '@/assets/imgs/login/green-card.webp';
import MobileCounterOfferImg from '@/assets/imgs/login/mobileCounterOffer.webp';
import AppImg from '@/assets/imgs/login-app-ads2.webp';
import LogoPepper from '@/assets/imgs/pepper-logo.svg';
import LoginScreen from '@/components/auth/LoginScreen/LoginScreen';
import LoginErrorModal from '@/components/auth/LoginErrorModal';
import {
  COUNTER_OFFER_EXPIRATION, GREEN_CARD_EXPIRATION, OPERATION_DASHBOARD, ROUTES_PATH,
} from '@/constants';
import ENVIRONMENT from '@/environments/environment';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import useModal from '@/hooks/useModal';
import { ErrorsInterface } from '@/interfaces/ErrorInterface/ErrorInterface';
import { checkIsLogged, saveExpiredCredentials, savePreviousPassword } from '@/redux/auth/auth';
import { postLeads, postToken } from '@/redux/auth/authAPI';
import { TokenRequest } from '@/redux/auth/authInterface';
import { addError } from '@/redux/error/error';
import {
  clearMonthSummaries,
  saveSettlements,
} from '@/redux/reporting/reporting';
import { clearBiometricCredentials, getBiometricCredentials } from '@/services/biometry';
import InsightService from '@/services/insights';
import { notificationService } from '@/services/notifications';
import { storageService } from '@/services/storage';
import TokenService from '@/services/token';
import { generateLoginTrackingParams, loginTrack } from '@/services/tracking';
import { isPasswordExpiredError, isWeakPassword } from '@/utils/password';
import { usernameValidation } from '@/utils/validators';

import styles from './Login.module.scss';
import LoginAd from './LoginAd/LoginAd';
import LoginError from './LoginError/LoginError';
import LoginPassword from './LoginPassword/LoginPassword';
import LoginResend from './LoginResend/LoginResend';
import LoginUsername from './LoginUsername/LoginUsername';

enum CurrentPage {
  passwordPage,
  userPage,
  passwordErrorPage,
  resetPassword,
}
export default function Login() {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const { presentModal: presentLoginErrorModal } = useModal(LoginErrorModal);

  const isFirstTime = storageService.getOnboardingStorage();
  const [biometryType, setBiometryType] = useState(storageService.getBiometrySaved);
  const [biometryUsernameMismatch, setBiometryUsernameMismatch] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [userParamError, setUserParamError] = useState<boolean>(false);
  const [loginErrorUnexpected, setLoginErrorUnexpected] = useState<boolean>(false);
  const [loginCookie] = useState(storageService.hasPepperMerchantLogin);
  const [currentPage, setCurrentPage] = useState<CurrentPage>(
    CurrentPage.userPage,
  );
  const searchParams = new URLSearchParams(location.search);
  const userChop = storageService.getEntryUserChop();

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Tablet);

  useEffect(() => {
    setIsMobile(!isTablet && !isDesktop);
  }, [isTablet, isDesktop]);

  const [pinFocused, setPinFocused] = useState<boolean>(false);
  const pinBlurTimeout = useRef<ReturnType<typeof setTimeout>>();
  const isPinInput = (element: Element | null) => !!element?.id?.startsWith('login-password-input');

  const handleCardFocus = (event: React.FocusEvent) => {
    if (isPinInput(event.target)) {
      clearTimeout(pinBlurTimeout.current);
      setPinFocused(true);
    }
  };

  const handleCardBlur = () => {
    // Se espera antes de recolocar el logo: si la tarjeta baja al instante, el toque
    // en "Atrás", "Confirmar" o "mi clave" cae fuera del botón y no hace nada
    clearTimeout(pinBlurTimeout.current);
    pinBlurTimeout.current = setTimeout(() => {
      setPinFocused(isPinInput(document.activeElement));
    }, 300);
  };

  useEffect(() => () => clearTimeout(pinBlurTimeout.current), []);

  const goToPassword = (user: string) => {
    setUsername(user);

    setCurrentPage(CurrentPage.passwordPage);
  };

  const goToUser = () => {
    setCurrentPage(CurrentPage.userPage);
  };

  // `unexpected`: error no previsto, el aviso no dice que los datos sean incorrectos
  const handleError = (unexpected = false) => {
    setLoginErrorUnexpected(unexpected);
    if (!isTablet && !isDesktop) {
      presentLoginErrorModal({
        removeSubtitle: unexpected,
        onDismiss: () => {
          setCurrentPage(CurrentPage.userPage);
        },
      });
    } else {
      setCurrentPage(CurrentPage.passwordErrorPage);
    }
  };

  const handleForgotPasswdLink = () => {
    setCurrentPage(CurrentPage.resetPassword);
  };

  const handleResendError = () => {
    if (!isTablet && !isDesktop) {
      presentLoginErrorModal({
        onDismiss: () => {
          setCurrentPage(CurrentPage.userPage);
        },
      });
    } else {
      setCurrentPage(CurrentPage.passwordErrorPage);
    }
  };

  useEffect(() => {
    if (isFirstTime !== 'false' && Capacitor.isNativePlatform()) {
      history.replace('/onboarding');
    }
  }, []);

  useEffect(() => {
    TokenService.clearSession();
    history.push(generateLoginTrackingParams(searchParams));
    dispatch(saveSettlements([]));
    dispatch(clearMonthSummaries());
    checkUserParams();
  }, []);
  const checkUserParams = async () => {
    if (userChop) {
      // Se elimina tras completar su uso para loguear al merchant
      storageService.clearEntryUserChop();
      setUsername(userChop);
      const userValid = usernameValidation(userChop);
      if (userValid && loginCookie) {
        setCurrentPage(CurrentPage.passwordPage);
      } else {
        if (!userValid) {
          setUserParamError(true);
        }
        setCurrentPage(CurrentPage.userPage);
      }
    } else {
      setCurrentPage(CurrentPage.userPage);
    }
  };

  const getLead = async () => {
    try {
      const leadsResponse = await postLeads();
      sessionStorage.setItem('leadToken', leadsResponse?.data.token);
      const expirationDate = new Date();
      expirationDate.setSeconds(
        expirationDate.getSeconds() + leadsResponse?.data.expires_in,
      );
      sessionStorage.setItem('expiresLeads', expirationDate.toUTCString());
    } catch (err) {
      dispatch(addError(''));
    }
  };

  const isInvalidCredentialsError = (error: any) => (
    error?.result?.errors?.some((e: ErrorsInterface) => e.code === 'USR0003')
    || [400, 401].includes(error?.result?.http_code)
  );

  // Demasiados intentos de login: el back responde 429 en vez de USR0003
  const isTooManyAttemptsError = (error: any) => (
    error?.result?.http_code === 429 || error?.response?.status === 429
  );

  const handleLogin = async (username: string, password: string, fromBiometry = false) => {
    const tokenData: TokenRequest = {
      grant_type: 'password',
      username,
      password,
      realm: 'app_merchant',
    };

    try {
      const tokenResponse = await postToken(tokenData);
      if (tokenResponse) {
        InsightService.logTrace('Login', [
          { Login: tokenResponse.result.info },
        ]);
      }
      const { access_token, refresh_token } = tokenResponse.data;
      const expirationDate = new Date(
        new Date().setHours(new Date().getHours() + 2),
      ).toString();
      localStorage.setItem('username', tokenData.username.toUpperCase());
      sessionStorage.setItem('expirationDate', expirationDate);
      sessionStorage.setItem('accessToken', access_token);
      sessionStorage.setItem('refreshToken', refresh_token);
      if (ENVIRONMENT.ENV && access_token) {
        loginTrack(tokenData.username.toUpperCase());
      }
      if (access_token) {
        getLead();
      }
      if (!notificationService.hasEnabledNotifications()) {
        LocalNotifications.checkPermissions().then((permission) => {
          if (permission.display === 'granted') {
            notificationService.enableNotifications();
          }
        });
      }
      if (isWeakPassword(password)) {
        // Clave sin los requisitos de seguridad: el router solo le deja cambiarla
        storageService.setMustChangePassword();
        // Para que el cambio no admita la misma clave con la que acaba de entrar
        dispatch(savePreviousPassword(password));
      }
      history.push(OPERATION_DASHBOARD);

      dispatch(checkIsLogged());
    } catch (error: any) {
      console.log('error', error);
      if (isPasswordExpiredError(error)) {
        // Hay que actualizar la clave antes de entrar. La biometría se renueva tras el cambio
        dispatch(saveExpiredCredentials({ username, password }));
        history.push(ROUTES_PATH.LOGIN_CHANGE_PASSWORD);
      } else if (fromBiometry && isInvalidCredentialsError(error)) {
        // Las credenciales guardadas ya no son válidas: se borran para que el usuario
        // entre con su clave y se le vuelva a ofrecer activar la biometría
        await clearBiometricCredentials().catch(() => {});
        setBiometryType(null);
        handleError();
      } else if (
        error?.result?.errors?.find((e: ErrorsInterface) => e.code === 'USR0003')
        || isTooManyAttemptsError(error)
      ) {
        handleError();
      } else {
        handleError(true);
      }
    }
  };

  const handleBiometryLogin = useCallback(() => {
    getBiometricCredentials().then((credentials) => {
      if (
        credentials
        && (credentials.username === username || username === '')
      ) {
        handleLogin(credentials.username, credentials.password, true);
      } else if (credentials && credentials.username !== username) {
        setBiometryUsernameMismatch(true);
        handleError();
      }
    });
  }, [handleLogin]);

  useEffect(() => {
    const autoBiometry = localStorage.getItem('autoBiometry');
    if (autoBiometry !== 'false' && biometryType) {
      handleBiometryLogin();
    }
  }, [biometryType]);

  return (
    <IonPage
      className={classNames(
        styles.loginBackground,
        currentPage === CurrentPage.userPage && 'margin-0',
      )}
    >
      <div className={styles.loginHeader}>
        <div className={styles.logoContainer}>
          <IonImg src={LogoPepper} className={styles.logoPepper} />
        </div>
      </div>

      <div
        className={classNames(styles.loginPage, pinFocused && styles.pinFocused)}
        id="login-page"
        onFocus={handleCardFocus}
        onBlur={handleCardBlur}
      >
        {!isPlatform('android')
          && !isPlatform('ios')
          && (COUNTER_OFFER_EXPIRATION ? (
            <div
              className={classNames(
                styles.loginScreen,
                styles.loginDesktop,
                styles.loginImgCounterOffer,
              )}
              id="counter-offer-login"
            >
              <IonImg src={MobileCounterOfferImg} />
            </div>
          ) : GREEN_CARD_EXPIRATION ? (
            <div
              className={classNames(
                styles.loginScreen,
                styles.loginDesktop,
                styles.loginGreenCard,
              )}
              id="green-card-login"
            >
              <IonImg src={GreenCardImg} className={styles.loginScreenImg} />
            </div>
          ) : (
            <div
              className={classNames(
                styles.loginScreen,
                styles.loginDesktop,
                styles.loginAppImg,
              )}
              id="app-card-login"
            >
              <IonImg src={AppImg} className={styles.loginScreenImg} />
              <LoginAd />
            </div>
          ))}

        <div
          className={classNames(
            styles.loginScreen,
            styles.loginDesktop,
            COUNTER_OFFER_EXPIRATION && styles.loginScreenCounterOffer,
          )}
        >
          <LoginScreen />
        </div>
        <div className={classNames(styles.loginScreen, styles.loginMobile)}>
          <IonImg src={LogoPepper} className={styles.loginScreenImg} />
          <IonLabel className={styles.loginScreenLabel}>Comercios</IonLabel>
        </div>
        <div
          className={classNames(
            styles.loginInfo,
            currentPage !== CurrentPage.userPage && 'h-100',
            currentPage === CurrentPage.resetPassword && styles.resetPassword,
          )}
        >
          <IonCard className={styles.loginCard}>
            {currentPage === CurrentPage.passwordPage && (
              <LoginPassword
                backBtn={goToUser}
                username={username}
                resentPassword={handleForgotPasswdLink}
                biometryUsernameMismatch={biometryUsernameMismatch}
                loginError={(unexpected) => handleError(unexpected)}
              />
            )}
            {currentPage === CurrentPage.userPage && (
              <LoginUsername
                nextBtn={goToPassword}
                username={username}
                onUsernameChange={(username) => setUsername(username)}
                userError={userParamError}
              />
            )}
            {currentPage === CurrentPage.passwordErrorPage && (
              <LoginError close={goToUser} removeSubtitle={loginErrorUnexpected} />
            )}
            {currentPage === CurrentPage.resetPassword && (
              <LoginResend
                username={username}
                setError={handleResendError}
                backBtn={() => setCurrentPage(CurrentPage.passwordPage)}
              />
            )}
          </IonCard>
          {biometryType !== null
            && !biometryUsernameMismatch
            && currentPage === CurrentPage.userPage && (
              <button
                type="button"
                onClick={handleBiometryLogin}
                className={styles.biometryButton}
              >
                <IonIcon
                  src={
                    biometryType === BiometryType.FACE_ID
                      ? FaceIdLogo
                      : FingerprintLogo
                  }
                />
                <span>
                  {biometryType === BiometryType.FACE_ID
                    ? 'Accede con Face ID'
                    : 'Accede con tu huella'}
                </span>
              </button>
          )}
        </div>
      </div>
    </IonPage>
  );
}
